import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Input from 'reactstrap/lib/Input';
import Label from 'reactstrap/lib/Label';
import { getFacetTrackingParams } from 'site-modules/shared/utils/inventory/tracking';
import { numberWithCommas } from 'site-modules/shared/utils/string';
import { buildFilterPixelValue } from 'client/site-modules/shared/utils/inventory-utils/build-filter-pixel-value';
import { formatPriceString } from 'site-modules/shared/utils/price-utils';

/**
 * Default checkbox.
 * @param {Boolean} selected
 * @param {String} classes
 * @param {Boolean} isContrastUpdate
 * @returns {ReactElement}
 */
export function DefaultCheckbox({ selected = false, classes = 'align-self-center', isContrastUpdate = false }) {
  return (
    <span
      className={classNames('d-inline-block checkbox', classes, {
        'icon-checkbox-checked2': selected && !isContrastUpdate,
        'icon-checkbox-unchecked2': !selected && !isContrastUpdate,
        'icon-checkbox-checked3': selected && isContrastUpdate,
        'icon-checkbox-unchecked3': !selected && isContrastUpdate,
      })}
      aria-hidden
    />
  );
}

DefaultCheckbox.propTypes = {
  selected: PropTypes.bool,
  classes: PropTypes.string,
  isContrastUpdate: PropTypes.bool,
};

/**
 * Color checkbox.
 * @param {string} color
 * @returns {ReactElement}
 */
export function ColorCheckbox({ color }) {
  return (
    <span className="checkbox checkbox-color d-flex justify-content-center align-items-center rounded-circle">
      <span className="rounded-circle" style={{ backgroundColor: `rgb(${color})` }} />
    </span>
  );
}

ColorCheckbox.propTypes = {
  color: PropTypes.string.isRequired,
};

/**
 * Renders color or default checkbox with name and count.
 * @param {object} facet
 * @param {function} onUpdate
 * @returns {ReactElement}
 */
export class Checkbox extends Component {
  static propTypes = {
    facet: PropTypes.shape({}).isRequired,
    onUpdate: PropTypes.func.isRequired,
    hideCount: PropTypes.bool,
    hideName: PropTypes.bool,
    disableTracking: PropTypes.bool,
    normalSize: PropTypes.bool,
    showPrice: PropTypes.bool,
    facetName: PropTypes.string.isRequired,
    checkboxClasses: PropTypes.string,
    labelClasses: PropTypes.string,
    isContrastUpdate: PropTypes.bool,
  };

  static defaultProps = {
    hideCount: false,
    hideName: false,
    disableTracking: false,
    showPrice: false,
    normalSize: false,
    checkboxClasses: '',
    labelClasses: '',
    isContrastUpdate: false,
  };

  handleFacetSelect = () => {
    const { onUpdate, facet, facetName } = this.props;
    onUpdate(
      {
        ...facet,
        type: facetName,
      },
      !facet.selected
    );
  };

  render() {
    const {
      facet,
      disableTracking,
      facetName,
      hideCount,
      hideName,
      normalSize,
      checkboxClasses,
      labelClasses,
      showPrice,
      isContrastUpdate,
    } = this.props;
    const { name, count = 0, selected, rgb, value, price = {} } = facet;
    const priceValue = price.usedTradeIn;
    const dataTrackingValue = buildFilterPixelValue(facetName, value, name);
    const trackingParams = getFacetTrackingParams(dataTrackingValue);
    const trackingData = disableTracking ? null : trackingParams;
    return (
      <li className="list-unstyled">
        <Label className={classNames('checkbox-label pos-r d-flex mb-1 mb-lg-0_5', labelClasses)} tabIndex={-1}>
          <Input
            onChange={this.handleFacetSelect}
            type="checkbox"
            className="input mt-0 pos-a"
            checked={!!selected}
            tabIndex={0}
            {...trackingData}
          />
          {rgb ? (
            <ColorCheckbox color={rgb} />
          ) : (
            <DefaultCheckbox classes={checkboxClasses} isContrastUpdate={isContrastUpdate} selected={selected} />
          )}
          {!hideName && (
            <span
              className={classNames('name me-0_25', {
                'ms-0_75': !rgb && !normalSize,
                'ms-1_5': rgb || normalSize,
                small: !normalSize,
              })}
            >
              {name}
            </span>
          )}
          {!hideCount && <span className="xsmall">({numberWithCommas(count)})</span>}
          {showPrice && priceValue && <span className="ms-0_75">{formatPriceString(priceValue)}</span>}
        </Label>
      </li>
    );
  }
}
